import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { io, Socket } from 'socket.io-client'
import { useAuthStore } from '../store/authStore'
import { useNotifStore } from '../store/notifStore'
import { invalidateIssueViews } from '../lib/queryInvalidation'
import { resolveSocketUrl } from '../lib/runtimeUrls'
import type { Notification } from '../types'

export function useSocket() {
  const socketRef = useRef<Socket | null>(null)
  const queryClient = useQueryClient()
  const { token, user } = useAuthStore()
  const { incrementUnread, pushToast } = useNotifStore()

  useEffect(() => {
    if (!token || !user) return

    const socketUrl = resolveSocketUrl(import.meta.env.VITE_SOCKET_URL)

    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => undefined)
    }

    const socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
    })

    socketRef.current = socket

    socket.on('connect', () => {
      socket.emit('join', { userId: user.id })
    })

    socket.on('notification', (notification: Notification) => {
      incrementUnread()
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'recent'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
      invalidateIssueViews(queryClient, notification.issue.project?.id)

      if (notification.type === 'ISSUE_ASSIGNED') {
        pushToast(notification)

        if (
          typeof window !== 'undefined' &&
          'Notification' in window &&
          Notification.permission === 'granted' &&
          document.visibilityState !== 'visible'
        ) {
          const browserNotification = new Notification('New assignment', {
            body: `${notification.issue.project?.name || 'Project'} · ${notification.issue.title} · ${notification.issue.priority.replace(/_/g, ' ')}`,
            tag: notification.id,
          })

          browserNotification.onclick = () => {
            window.focus()
            window.location.assign(`/issues/${notification.issue.id}`)
            browserNotification.close()
          }
        }
      }
    })

    socket.on('connect_error', (err) => {
      console.warn('Socket connection error:', err.message)
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [token, user?.id, incrementUnread, pushToast, queryClient])

  return socketRef.current
}
